import classNames from "classnames";
import React, { FC, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { LuChevronRight } from "react-icons/lu";
import { useNavigate } from "react-router-dom";

import { useAppSelector } from "../../../redux/redux";
import { IUser } from "../../../types/types";
import {
  clearWelcome,
  readWelcome,
  WELCOME_KEY,
  welcomeFor,
  WelcomeNote,
} from "./journey.utils";
import { Confetti } from "./JourneyParts";

type Step = {
  icon: string;
  title: string;
  text: string;
  to?: string;
  done?: boolean;
  help?: boolean;
};

const memberSteps: Step[] = [
  {
    icon: "💼",
    title: "See your work",
    text: "Work assigned to you and your roles.",
    to: "/works/my",
  },
  {
    icon: "✉️",
    title: "Connect your email",
    text: "See client emails right inside ProCharted.",
    to: "/mail",
  },
  {
    icon: "🎨",
    title: "Pick your colour",
    text: "So the team can spot your work at a glance.",
    to: "/user",
  },
  {
    icon: "❓",
    title: "Ask anything",
    text: "The round Help button (bottom right) answers questions.",
    help: true,
  },
];

const ownerSteps = (invited: number): Step[] => [
  invited > 0
    ? {
        icon: "✓",
        title: "Invite your team",
        text: `${invited} invitation${invited === 1 ? "" : "s"} sent`,
        done: true,
      }
    : {
        icon: "👥",
        title: "Invite your team",
        text: "Settings › Team › New User.",
        to: "/settings/team/users?invite=1",
      },
  {
    icon: "👤",
    title: "Add your clients",
    text: "One by one, or import a spreadsheet.",
    to: "/contacts",
  },
  {
    icon: "💼",
    title: "Create your first work item",
    text: "A tax return, a bookkeeping month… with a deadline.",
    to: "/works/all",
  },
  {
    icon: "✉️",
    title: "Connect your email",
    text: "See client emails next to their work.",
    to: "/mail",
  },
  {
    icon: "🖼️",
    title: "Add your logo",
    text: "It goes on your invoices.",
    to: "/settings/billing/billing-logo",
  },
];

/**
 * The first sign-in after joining (owner 2026-10-25): confetti + a welcome window, once.
 * Team members: written by the invitation page in this browser (matched by email).
 * New firms: written when the firm setup finishes.
 */
export const WelcomeModal: FC = () => {
  const user = useAppSelector((state) => state.appReducer.user) as
    | IUser
    | undefined;
  const navigate = useNavigate();
  const [note, setNote] = useState<WelcomeNote | null>(null);

  useEffect(() => {
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(WELCOME_KEY);
    } catch {
      raw = null;
    }
    const read = readWelcome(raw);
    const email = user?.userProfile?.email;
    if (!read) {
      if (raw) clearWelcome();
      return;
    }
    if (!email) return; // wait for the signed-in person
    if (welcomeFor(read, email)) {
      setNote(read);
      clearWelcome();
    }
  }, [user?.userProfile?.email]);

  if (!note) return null;

  const firstName = user?.userProfile?.firstName || "";
  const steps = note.kind === "owner" ? ownerSteps(note.invited) : memberSteps;
  const doneCount = steps.filter((step) => step.done).length;
  const close = () => setNote(null);
  const open = (step: Step) => {
    if (step.done) return;
    close();
    if (step.help) {
      (
        document.querySelector("[data-help-chat-button]") as HTMLElement | null
      )?.click();
      return;
    }
    if (step.to) navigate(step.to);
  };

  return createPortal(
    <>
      <Confetti />
      <div
        className={
          "fixed inset-0 z-[1000] flex items-center justify-center bg-[rgba(17,20,34,0.5)] p-4 font-Manrope"
        }
        onMouseDown={(event) =>
          event.target === event.currentTarget && close()
        }>
        <div
          role={"dialog"}
          aria-modal={"true"}
          aria-label={"Welcome"}
          className={
            "max-h-[calc(100vh-32px)] w-[620px] max-w-full overflow-y-auto rounded-[20px] bg-white text-center shadow-[0_30px_80px_rgba(0,0,0,0.3)]"
          }>
          <div
            className={
              "bg-gradient-to-br from-[#6B4EFF] to-[#8F6BFF] px-6 pb-7 pt-8 text-white"
            }>
            <div className={"text-[52px] leading-none"} aria-hidden={true}>
              🎉
            </div>
            <div className={"mt-3 text-[26px] font-extrabold leading-tight"}>
              {note.kind === "owner"
                ? `${note.firmName} is live!`
                : `Welcome to ${note.firmName}${
                    firstName ? `, ${firstName}` : ""
                  }!`}
            </div>
            <div className={"mt-2 text-[15px] opacity-90"}>
              {note.kind === "owner"
                ? `Welcome to ProCharted${
                    firstName ? `, ${firstName}` : ""
                  }. Your free trial has started.`
                : "You're all set up. Your team can now assign work to you."}
            </div>
          </div>
          <div className={"px-6 pb-6 pt-5 text-left sm:px-7"}>
            <div className={"mb-3 flex items-center justify-between"}>
              <span
                className={
                  "text-xs font-bold uppercase tracking-[0.06em] text-[#8A90A2]"
                }>
                {note.kind === "owner" ? "Get set up" : "A good place to start"}
              </span>
              {note.kind === "owner" && (
                <span className={"text-[13px] font-bold text-[#6B4EFF]"}>
                  {doneCount} of {steps.length} done
                </span>
              )}
            </div>
            {note.kind === "owner" && (
              <div className={"mb-3.5 h-1.5 rounded bg-[#EEF0F4]"}>
                <div
                  className={"h-1.5 rounded bg-[#22A65A]"}
                  style={{ width: `${(doneCount / steps.length) * 100}%` }}
                />
              </div>
            )}
            <div className={"grid gap-2.5"}>
              {steps.map((step) => (
                <button
                  key={step.title}
                  type={"button"}
                  onClick={() => open(step)}
                  disabled={step.done}
                  className={classNames(
                    "flex items-center gap-3.5 rounded-xl border px-3.5 py-3 text-left",
                    step.done
                      ? "border-[#D6EFE0] bg-[#F6FBF8]"
                      : "border-[#EDEFF3] hover:border-[#C9C0FB] hover:bg-[#FAF8FF]",
                  )}>
                  <span
                    className={classNames(
                      "flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px] text-lg",
                      step.done
                        ? "bg-[#E3F6EC] text-[#1E9E5A]"
                        : "bg-[#F3F0FF]",
                    )}
                    aria-hidden={true}>
                    {step.icon}
                  </span>
                  <span className={"min-w-0 flex-1"}>
                    <span
                      className={classNames(
                        "block font-bold",
                        step.done
                          ? "text-[#7A7F8C] line-through"
                          : "text-[#1F2433]",
                      )}>
                      {step.title}
                    </span>
                    <span className={"block text-[13px] text-[#7A7F8C]"}>
                      {step.text}
                    </span>
                  </span>
                  {!step.done && (
                    <LuChevronRight className={"text-[#8A90A2]"} />
                  )}
                </button>
              ))}
            </div>
            <button
              type={"button"}
              onClick={close}
              className={
                "mt-5 flex h-12 w-full items-center justify-center rounded-[10px] bg-[#6B4EFF] text-[15px] font-bold text-white shadow-[0_4px_14px_rgba(107,78,255,0.28)] hover:bg-[#5A3EEB]"
              }>
              Let&apos;s go
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};
